import { COMPANY_SIZE_OPTIONS, INDUSTRY_OPTIONS, ROLE_OPTIONS } from '../../../shared/constants/profileOptions';

/**
 * Waiting-list questionnaire — three single-choice questions followed by one
 * free-text step, verbatim from Figma (MVP-DESIGNES 9141:2939 .. 9144:3246).
 *
 * The choice labels double as the stored values: the backend
 * (`optuneutils.parameter_validators.platform_access_params.WAITLIST_QUESTION_OPTIONS`)
 * validates each answer against the exact same strings (shared through
 * `shared/constants/profileOptions.js`), so keep both lists in sync when a
 * question changes.
 *
 * Options are listed in Figma reading order (row by row across the two
 * columns); `splitIntoColumns` turns that into the column-first layout the
 * frames use.
 */

export const WAITLIST_QUESTIONS = [
    { key: 'role', question: 'What is your role?', options: ROLE_OPTIONS },
    { key: 'industry', question: 'What industry are you in?', options: INDUSTRY_OPTIONS },
    { key: 'company_size', question: 'What is your company size?', options: COMPANY_SIZE_OPTIONS },
];

/**
 * Last step (Figma 9144:3246): an optional free-text answer stored in the
 * request's `reason`.  `maxLength` mirrors the backend's free-text limit.
 */
export const WAITLIST_REASON_STEP = Object.freeze({
    key: 'reason',
    question: 'Why should we pick you? (Optional)',
    placeholder: 'Tell us a bit about your needs to help us understand how we can best support you.',
    maxLength: 2000,
});

/** Every wizard step in order: the choice questions, then the free-text step. */
export const WAITLIST_STEPS = [...WAITLIST_QUESTIONS, WAITLIST_REASON_STEP];

export const WAITLIST_STEP_COUNT = WAITLIST_STEPS.length;

/** Column labels shown in the god-only Waiting List table, keyed by answer field. */
export const WAITLIST_ANSWER_LABELS = {
    role: 'Role',
    industry: 'Industry',
    company_size: 'Company size',
    reason: 'Why should we pick you?',
};

/** Empty answer set: every step unanswered (`null` = skipped). */
export const emptyWaitlistAnswers = () => Object.fromEntries(WAITLIST_STEPS.map(({ key }) => [key, null]));

/** Free text as the backend stores it: trimmed, capped, `null` when blank. */
export const normalizeWaitlistReason = (value) => {
    const text = String(value ?? '')
        .trim()
        .slice(0, WAITLIST_REASON_STEP.maxLength);
    return text || null;
};

/**
 * Split a reading-order option list into two columns the way Figma lays the
 * tiles out: options alternate left / right, so the left column holds the even
 * indexes and the right column the odd ones.
 */
export const splitIntoColumns = (options) => [
    options.filter((_, index) => index % 2 === 0),
    options.filter((_, index) => index % 2 === 1),
];

/** Mirrors the backend regex: one "@" with text on both sides and a dotted domain. */
export const WAITLIST_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidWaitlistEmail = (value) => WAITLIST_EMAIL_PATTERN.test(String(value || '').trim());
